import React, { useEffect, useMemo, useRef, useState } from "react";
import TicketSelect from "../components/TicketSelect";
import TicketSummaryCard from "../components/TicketSummaryCard";
import ProjectDetailsEditor from "../components/ProjectDetailsEditor";
import { pick } from "../utils/fieldExtractors";
import { useProjectTickets } from "../hooks/useProjectTickets";
import { useProject } from "../hooks/useProject";
import { useMsal } from "@azure/msal-react";
import { SignInButton } from "../components/ui/SignInButton";
import { getProjectTicketsUrl, getConfigTypesUrl, getCreateConfigUrl, getInstallationTicketsUrl, getAgreementsUrlByQuote, getAttachAgreementUrl, getProcurementProductsUrl, getScheduledResourcesUrl } from "../apiConfig";
import { loginRequest } from "../authConfig";
import { InteractionStatus } from "@azure/msal-browser";

export default function ProjectTicketsPage() {
  const conditions = useMemo(
    () => 'summary like "*Transfer to Support*" and status/name != "Complete" and owner/name != ""',
    []
  );

  // Build endpoint locally; no external config needed
  const endpoint = useMemo(() => getProjectTicketsUrl(conditions), [conditions]);

  const {instance, accounts, inProgress} = useMsal();
    const [apiKey, setApiKey] = useState(null);
  useEffect(() => {
    async function fetchKey() {
      const request = {
        ...loginRequest,
        account: accounts[0],
      };
      if (inProgress === InteractionStatus.None) {
        // Silently acquires an access token which is then attached to a request for Microsoft Graph data
        await instance
          .acquireTokenSilent(request)
          .then((response) => {
            setApiKey(response.idToken);
          })
          .catch(async (e) => {
            console.log(e);
            await instance.acquireTokenPopup(request).then((response) => {
              setApiKey(response.idToken);
            });
          });
      }
    }
    fetchKey();
  }, [instance, accounts, inProgress]);

  // Refresh key to trigger manual/interval ticket reloads
  const [ticketsRefreshKey, setTicketsRefreshKey] = useState(0);
  const { tickets, loading, error } = useProjectTickets({ endpoint, token: apiKey, refreshKey: ticketsRefreshKey });
  // Interval-based auto refresh (30 minutes) when authenticated
  useEffect(() => {
    if (!apiKey) return; // only when token present
    const intervalMs = 30 * 60 * 1000; // 30 minutes
    const id = setInterval(() => {
      setTicketsRefreshKey(k => k + 1);
    }, intervalMs);
    return () => clearInterval(id);
  }, [apiKey]);
  const [selectedId, setSelectedId] = useState("");
  // Toggle between ticket dropdown search and project id search
  const [searchMode, setSearchMode] = useState("ticket"); // 'ticket' | 'project'
  const [projectSearchInput, setProjectSearchInput] = useState("");
  const [projectSearchId, setProjectSearchId] = useState("");

  useEffect(() => {
    if (searchMode !== "ticket") {
      // In project search mode, do not auto-fill any ticket selection
      setSelectedId("");
      return;
    }
    if (tickets.length > 0) {
      const firstId = pick(tickets[0], "Id") ?? pick(tickets[0], "id");
      if (firstId !== undefined) setSelectedId(String(firstId));
    } else {
      setSelectedId("");
    }
  }, [tickets, searchMode]);

  const selectedTicket = useMemo(() => {
    if (searchMode === "project") return null;
    return tickets.find((t) => String(pick(t, "Id") ?? pick(t, "id")) === selectedId);
  }, [tickets, selectedId, searchMode]);

  // Extract project id from selected ticket
  const selectedProjectId = useMemo(() => {
    if (searchMode === "project") return projectSearchId || "";
    if (!selectedTicket) return "";
    return pick(selectedTicket, "Project.Id") ?? pick(selectedTicket, "project.id") ?? "";
  }, [selectedTicket, searchMode, projectSearchId]);

  const { project, warrantyStart, quoteId: projectQuoteId, loading: projectLoading, error: projectError } = useProject({ projectId: selectedProjectId, token: apiKey });

  // Procurement products for Room Name extraction
  const procurementEndpoint = useMemo(() => selectedProjectId ? getProcurementProductsUrl(selectedProjectId) : "", [selectedProjectId]);
  const { tickets: procurementProducts } = useProjectTickets({ endpoint: procurementEndpoint, token: apiKey });

  const roomNames = useMemo(() => {
    const results = new Set();
    if (Array.isArray(procurementProducts)) {
      for (const prod of procurementProducts) {
        const cfs = pick(prod, "CustomFields") ?? pick(prod, "customFields") ?? [];
        if (Array.isArray(cfs)) {
          for (const cf of cfs) {
            const caption = (pick(cf, "Caption") ?? pick(cf, "caption") ?? "").toLowerCase();
            if (caption === "room name") {
              const val = pick(cf, "Value") ?? pick(cf, "value") ?? "";
              if (val && String(val).trim()) results.add(String(val).trim());
            }
          }
        }
      }
    }
    return Array.from(results).sort((a,b) => a.localeCompare(b));
  }, [procurementProducts]);

  const [projectFormState, setProjectFormState] = useState({});

  // Fetch Installation tickets for this project to derive owner
  const installationEndpoint = useMemo(() => {
    if (!selectedProjectId) return "";
    return getInstallationTicketsUrl(selectedProjectId);
  }, [selectedProjectId]);

  const { tickets: installationTickets } = useProjectTickets({ endpoint: installationEndpoint, token: apiKey });

  const installationOwner = useMemo(() => {
    if (!Array.isArray(installationTickets) || installationTickets.length === 0) return { id: "", name: "" };
    const t = installationTickets[0];
    const owner = (pick(t, "Owner") ?? pick(t, "owner")) || {};
    const id = pick(owner, "Id") ?? pick(owner, "id") ?? "";
    const name = pick(owner, "Name") ?? pick(owner, "name") ?? "";
    return { id: String(id), name: String(name) };
  }, [installationTickets]);

  // Fetch scheduled resources (members) for installation ticket
  const installationTicketId = useMemo(() => {
    if (!Array.isArray(installationTickets) || installationTickets.length === 0) return "";
    const t = installationTickets[0];
    const id = pick(t, "Id") ?? pick(t, "id") ?? "";
    return String(id || "");
  }, [installationTickets]);

  const scheduledResourcesEndpoint = useMemo(() => installationTicketId ? getScheduledResourcesUrl(installationTicketId) : "", [installationTicketId]);
  const { tickets: scheduledResources } = useProjectTickets({ endpoint: scheduledResourcesEndpoint, token: apiKey });
  const installationMembers = useMemo(() => {
    const list = Array.isArray(scheduledResources) ? scheduledResources : [];
    return list.map(r => {
      const member = (pick(r, "Member") ?? pick(r, "member")) || {};
      const id = pick(member, "Id") ?? pick(member, "id");
      const name = pick(member, "Name") ?? pick(member, "name") ?? "";
      return { id, name };
    }).filter(m => m.id !== undefined && m.id !== null);
  }, [scheduledResources]);

  // Try to derive installation date from the installation ticket first
  const installationDateFromTicket = useMemo(() => {
    if (!Array.isArray(installationTickets) || installationTickets.length === 0) return "";
    const t = installationTickets[0];
    const cfs = (pick(t, "CustomFields") ?? pick(t, "customFields") ?? []);
    if (Array.isArray(cfs)) {
      const match = cfs.find(cf => {
        const caption = (pick(cf, "Caption") ?? pick(cf, "caption") ?? "");
        return String(caption).toLowerCase() === "installation date" || String(caption).toLowerCase() === "install date";
      });
      if (match) {
        const val = pick(match, "Value") ?? pick(match, "value") ?? "";
        if (val) return String(val);
      }
    }
    const actualStart = pick(t, "ActualStart") ?? pick(t, "actualStart") ?? "";
    return actualStart ? String(actualStart) : "";
  }, [installationTickets]);

  // Extract Site from the selected ticket: keep id, show name (fallback to SiteName field if needed)
  const siteInfo = useMemo(() => {
    // In ticket mode, derive from the selected ticket
    if (searchMode === "ticket") {
      if (!selectedTicket) return { id: "", name: "" };
      const siteObj = (pick(selectedTicket, "Site") ?? pick(selectedTicket, "site")) || {};
      const id = pick(siteObj, "Id") ?? pick(siteObj, "id") ?? "";
      let name = pick(siteObj, "Name") ?? pick(siteObj, "name") ?? "";
      if (!name) {
        name = pick(selectedTicket, "SiteName") ?? pick(selectedTicket, "siteName") ?? "";
      }
      return { id: String(id || ""), name: String(name || "") };
    }
    // In project search mode, derive from the installation ticket's site.name
    if (Array.isArray(installationTickets) && installationTickets.length > 0) {
      const t = installationTickets[0];
      const siteObj = (pick(t, "Site") ?? pick(t, "site")) || {};
      const id = pick(siteObj, "Id") ?? pick(siteObj, "id") ?? "";
      const name = pick(siteObj, "Name") ?? pick(siteObj, "name") ?? "";
      return { id: String(id || ""), name: String(name || "") };
    }
    return { id: "", name: "" };
  }, [searchMode, selectedTicket, installationTickets]);

  // Fetch configuration types (inactiveFlag=false)
  const configTypesEndpoint = useMemo(() => getConfigTypesUrl("inactiveFlag=false"), []);
  const { tickets: configTypesRaw } = useProjectTickets({ endpoint: configTypesEndpoint, token: apiKey });

  // Normalize config types list and compute default selection: prefer id 130, else name "AV Space"
  const { configTypes, defaultConfigTypeId } = useMemo(() => {
    const items = Array.isArray(configTypesRaw) ? configTypesRaw : [];
    const preferredById = items.find((x) => String(pick(x, "Id") ?? pick(x, "id")) === "130");
    let defId = preferredById ? String(pick(preferredById, "Id") ?? pick(preferredById, "id")) : "";
    if (!defId) {
      const byName = items.find((x) => String(pick(x, "Name") ?? pick(x, "name")).toLowerCase() === "av space");
      if (byName) defId = String(pick(byName, "Id") ?? pick(byName, "id"));
    }
    return { configTypes: items, defaultConfigTypeId: defId };
  }, [configTypesRaw]);

  // ----- Create Configuration Logic -----
  const [createStatus, setCreateStatus] = useState("idle"); // idle | posting | success | error
  const [createError, setCreateError] = useState("");
  const [createdConfig, setCreatedConfig] = useState(null);
  const [selectedAgreementId, setSelectedAgreementId] = useState("");
  const agreementsRef = useRef(null);
  const [attachStatus, setAttachStatus] = useState("idle"); // idle | posting | success | error
  const [attachError, setAttachError] = useState("");

  // Reset create/agreements state when changing the selected ticket (start fresh)
  useEffect(() => {
    setCreateStatus("idle");
    setCreateError("");
    setCreatedConfig(null);
    setSelectedAgreementId("");
    setAttachStatus("idle");
    setAttachError("");
    // Also reset room defaults via changing key props: handled by passing roomNameDefault/roomNumberDefault
  }, [selectedId]);

  const payload = useMemo(() => {
    if (!project || !selectedProjectId) return null;
    const {
      configurationName,
      configTypeId,
      companyId,
      siteId,
      installationOwnerId,
      warrantyStart,
      roomName,
      roomNumber,
    } = projectFormState || {};
    if (!configurationName) return null;
    return {
      name: configurationName,
      type: configTypeId ? { id: Number(configTypeId) } : undefined,
      company: companyId ? { id: Number(companyId) } : undefined,
      site: siteId ? { id: Number(siteId) } : undefined,
      installationDate: warrantyStart || "",
      installedBy: installationOwnerId ? { id: Number(installationOwnerId) } : undefined,
      // Support department
      department: { id: 12 },
      customFields: [
        { id: 57, value: roomName || "" },
        { id: 56, value: roomNumber || "" },
      ],
    };
  }, [project, selectedProjectId, projectFormState]);

  async function handleCreate() {
    if (!apiKey) return;
    if (!payload) return;
    setCreateStatus("posting");
    setCreateError("");
    setCreatedConfig(null);
    try {
      const url = getCreateConfigUrl();
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${apiKey}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Request failed: ${res.status}`);
      }
      const json = await res.json().catch(() => ({}));
      setCreatedConfig(json);
      const newId = pick(json, "Id") ?? pick(json, "id");
      if (newId === undefined || newId === null || String(newId).trim() === "") {
        setCreateError("Failure configuration not created");
        setCreateStatus("error");
      } else {
        setCreateStatus("success");
      }
    } catch (e) {
      setCreateError(e.message || String(e));
      setCreateStatus("error");
    }
  }

  // ----- Agreements (shown after successful create) -----
  const agreementsEndpoint = useMemo(() => {
    if (createStatus !== "success") return "";
    if (!projectQuoteId) return "";
    return getAgreementsUrlByQuote(projectQuoteId);
  }, [createStatus, projectQuoteId]);

  const { tickets: agreementsRaw } = useProjectTickets({ endpoint: agreementsEndpoint, token: apiKey });

  const agreements = useMemo(() => Array.isArray(agreementsRaw) ? agreementsRaw : [], [agreementsRaw]);

  useEffect(() => {
    if (!agreements || agreements.length === 0) {
      setSelectedAgreementId("");
      return;
    }
    const firstId = pick(agreements[0], "Id") ?? pick(agreements[0], "id");
    if (firstId !== undefined && firstId !== null) setSelectedAgreementId(String(firstId));
  }, [agreements]);

  // Scroll to the agreements section when it becomes available
  useEffect(() => {
    if (createStatus === "success" && agreementsRef.current) {
      agreementsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [createStatus]);

  return (
    <div className="st-container">
      <h2 className="st-title">Project Tickets</h2>

  {/* Removed endpoint debug display */}

      {!((instance.getActiveAccount() || accounts[0]) && apiKey) && (
        <div className="st-card" style={{ marginBottom: 12 }}>
          <div className="st-card-title">Sign in required</div>
          <div style={{ marginBottom: 8 }}>
            Please sign in to retrieve tickets.
          </div>
          <SignInButton />
        </div>
      )}

      {loading && <div>Loading tickets…</div>}
      {error && <div className="st-error">{error}</div>}

      <div className="st-row">
        <label htmlFor="ticketSelect" className="st-strong">
          {searchMode === "ticket" ? "Choose a ticket:" : "Search by project: RR"}
        </label>
        {searchMode === "ticket" ? (
          <TicketSelect
            tickets={tickets}
            value={selectedId}
            onChange={setSelectedId}
            disabled={loading || tickets.length === 0}
          />
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="text"
              className="st-input"
              placeholder="Enter Project ID (digits only, e.g., 12345)"
              value={projectSearchInput}
              onChange={(e) => setProjectSearchInput(e.target.value)}
              style={{ width: 220 }}
            />
            <button
              type="button"
              className="st-button"
              disabled={!projectSearchInput.trim()}
              onClick={() => setProjectSearchId(projectSearchInput.trim())}
            >
              Search
            </button>
          </div>
        )}
        <button
          type="button"
          className="st-button"
          style={{ marginLeft: 8 }}
          disabled={loading || !apiKey}
          onClick={() => setTicketsRefreshKey(k => k + 1)}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
        <button
          type="button"
          className="st-button"
          style={{ marginLeft: 8 }}
          onClick={() => {
            const next = searchMode === "ticket" ? "project" : "ticket";
            setSearchMode(next);
            // Clear project search state when toggling back to ticket mode
            if (next === "ticket") {
              setProjectSearchInput("");
              setProjectSearchId("");
            }
          }}
        >
          {searchMode === "ticket" ? "Search by Project" : "Search by Ticket"}
        </button>
      </div>

      {(searchMode === "ticket" ? Boolean(selectedTicket) : Boolean(projectSearchId)) && (
        <TicketSummaryCard
          ticket={searchMode === "ticket" ? selectedTicket : null}
          projectQuoteId={projectQuoteId}
          roomNames={roomNames}
          projectIdOverride={selectedProjectId}
          projectNameOverride={project ? (pick(project, "Name") ?? pick(project, "name") ?? "") : ""}
        />
      )}

      {selectedProjectId && (
        <>
          {projectLoading && <div>Loading project…</div>}
          {projectError && <div className="st-error">{projectError}</div>}
          <ProjectDetailsEditor
            project={project}
            warrantyStart={installationDateFromTicket || warrantyStart}
            ownerName={installationOwner.name}
            ownerId={installationOwner.id}
            owners={installationMembers}
            configTypes={configTypes}
            defaultConfigTypeId={defaultConfigTypeId}
            siteName={siteInfo.name}
            siteId={siteInfo.id}
            configurationNameDefault={
              project ? (pick(project, "Name") ?? pick(project, "name") ?? "") : ""
            }
            roomNameDefault={selectedProjectId ? `RR${selectedProjectId}` : ""}
            roomNumberDefault={""}
            onChange={setProjectFormState}
            onCreate={handleCreate}
            canCreate={Boolean(payload) && createStatus !== "posting"}
            createStatus={createStatus}
            createError={createError}
            createdConfig={createdConfig}
          />
        </>
      )}

      {createStatus === "success" && (
        <div ref={agreementsRef} className="st-card" style={{ marginTop: 16 }}>
          <div className="st-card-title">Agreements</div>
          {!projectQuoteId && <div className="st-note">No Quote # found on project.</div>}
          {projectQuoteId && (
            <div className="st-row" style={{ marginTop: 8 }}>
              <label htmlFor="agreementSelect" className="st-strong">Select an agreement:</label>
              <select
                id="agreementSelect"
                className="st-select"
                disabled={!agreements || agreements.length === 0}
                value={selectedAgreementId}
                onChange={(e) => setSelectedAgreementId(e.target.value)}
              >
                {(!agreements || agreements.length === 0) ? (
                  <option value="">No agreements found</option>
                ) : (
                  agreements.map(a => {
                    const id = pick(a, "Id") ?? pick(a, "id");
                    const name = pick(a, "Name") ?? pick(a, "name") ?? "";
                    return (
                      <option key={String(id)} value={String(id)}>{name}</option>
                    );
                  })
                )}
              </select>
              <button
                type="button"
                className="st-button"
                disabled={!selectedAgreementId || !createdConfig?.id || attachStatus === "posting"}
                onClick={async () => {
                  if (!selectedAgreementId || !createdConfig?.id || !apiKey) return;
                  setAttachStatus("posting");
                  setAttachError("");
                  try {
                    const url = getAttachAgreementUrl(selectedAgreementId);
                    const res = await fetch(url, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `${apiKey}`,
                      },
                      body: JSON.stringify({ id: createdConfig.id }),
                    });
                    if (!res.ok) {
                      const t = await res.text();
                      throw new Error(t || `Attach failed (${res.status})`);
                    }
                    const json = await res.json().catch(() => ({}));
                    const returnedId = pick(json, "Id") ?? pick(json, "id");
                    if (String(returnedId) === String(createdConfig.id)) {
                      setAttachStatus("success");
                      // After attach success, blank Room Number and set Room Name to RR<ProjectId>
                      setProjectFormState((prev) => ({
                        ...prev,
                        roomNumber: "",
                        roomName: selectedProjectId ? `RR${selectedProjectId}` : (prev.roomName || ""),
                      }));
                    } else {
                      throw new Error("Response did not match configuration id");
                    }
                  } catch (e) {
                    setAttachError(e.message || String(e));
                    setAttachStatus("error");
                  }
                }}
                style={{ marginLeft: 8 }}
              >
                {attachStatus === "posting" ? "Attaching..." : "Attach"}
              </button>
            </div>
          )}
          {attachStatus === "error" && <div className="st-error" style={{ marginTop: 8 }}>{attachError}</div>}
          {attachStatus === "success" && <div className="st-success" style={{ marginTop: 8 }}>Agreement attached successfully.</div>}
        </div>
      )}
    </div>
  );
}

// Auto-refresh tickets every 30 minutes while component is mounted
// Keep outside main component? We need access to state; easier inside component via effect.
